import React, { useState } from 'react';
import { Lock, BarChart3, Download, Loader2, RefreshCw, AlertTriangle, MessageSquare } from 'lucide-react';

// Session-only. The password is held in component state so it is gone on
// reload and never written to localStorage.
const AdminResults = () => {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [openResponses, setOpenResponses] = useState(null);

  const load = async (pw) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/summary', { headers: { 'x-admin-password': pw } });
      if (res.status === 401) { setError('Incorrect password.'); setLoading(false); return; }
      if (res.status === 503) { setError('Admin access is not configured on the server. Set the ADMIN_PASSWORD config var.'); setLoading(false); return; }
      if (!res.ok) { setError('Could not load results.'); setLoading(false); return; }
      const json = await res.json();
      setData(json);
      setAuthed(true);
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch('/api/admin/export.csv', { headers: { 'x-admin-password': password } });
      if (!res.ok) { setError('Export failed.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `civicspark-responses-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed.');
    }
  };

  // ── Login ──────────────────────────────────────────────────────────────────

  if (!authed) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-600 rounded-2xl">
              <Lock className="text-white" size={22} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Survey Results</h2>
          </div>
          <p className="text-gray-500 mb-6 text-sm leading-relaxed">
            These results contain responses from high school students, including
            family immigration status. Access is restricted.
          </p>
          <form onSubmit={e => { e.preventDefault(); load(password); }}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="w-full p-4 rounded-2xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none mb-4"
            />
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-2xl mb-4 text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={!password || loading}
              className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />}
              <span>{loading ? 'Checking...' : 'View Results'}</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Results ────────────────────────────────────────────────────────────────

  const Bar = ({ option, count, total }) => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
      <div className="mb-2.5">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-700">{option}</span>
          <span className="text-gray-500 font-medium tabular-nums">{count} · {pct}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const fmtDate = d => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

  return (
    <div className="space-y-8">
      <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-white/20 rounded-2xl">
            <BarChart3 size={24} />
          </div>
          <h2 className="text-3xl font-bold">Survey Results</h2>
        </div>
        <p className="opacity-90 text-lg">
          {data.total} {data.total === 1 ? 'response' : 'responses'}
          {data.total > 0 && ` · ${fmtDate(data.firstResponse)} to ${fmtDate(data.latestResponse)}`}
        </p>
      </div>

      {data.questionSource !== 'sheet' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl flex items-start gap-3">
          <AlertTriangle size={20} className="shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Questions are served from the built-in defaults, not your Google Sheet.</strong>
            <br />
            Set the <code className="bg-amber-100 px-1 rounded">SURVEY_SHEET_URL</code> config var to make sheet edits take effect.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={downloadCsv}
          className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-green-700 transition-all"
        >
          <Download size={18} />
          <span>Export CSV</span>
        </button>
        <button
          onClick={() => load(password)}
          className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-2xl font-semibold border border-gray-200 hover:bg-white/80 transition-all"
        >
          <RefreshCw size={18} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl">{error}</div>
      )}

      {data.total === 0 ? (
        <div className="bg-white/90 backdrop-blur-sm p-12 rounded-3xl shadow-lg border border-gray-100/50 text-center">
          <p className="text-gray-500 text-lg">No responses yet.</p>
        </div>
      ) : (
        data.summary.map(q => (
          <div key={q.fieldName} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50">
            <div className="mb-5">
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{q.stepTitle}</span>
              <h3 className="text-lg font-bold text-gray-900 mt-1 leading-snug">{q.text}</h3>
              <p className="text-sm text-gray-400 mt-1">
                {q.answered} of {data.total} answered
                {q.type === 'scale' && q.mean !== null && ` · mean ${q.mean.toFixed(2)} / 5`}
              </p>
            </div>

            {q.type === 'textarea' ? (
              q.responses.length === 0 ? (
                <p className="text-gray-400 italic text-sm">No written responses yet.</p>
              ) : (
                <div className="space-y-3">
                  {(openResponses === q.fieldName ? q.responses : q.responses.slice(0, 5)).map((t, i) => (
                    <div key={i} className="flex gap-3 bg-gray-50 p-4 rounded-2xl">
                      <MessageSquare size={16} className="text-gray-400 shrink-0 mt-0.5" />
                      <p className="text-gray-700 text-sm leading-relaxed">{t}</p>
                    </div>
                  ))}
                  {q.responses.length > 5 && openResponses !== q.fieldName && (
                    <button
                      onClick={() => setOpenResponses(q.fieldName)}
                      className="text-blue-600 text-sm font-semibold hover:underline"
                    >
                      Show all {q.responses.length} responses
                    </button>
                  )}
                </div>
              )
            ) : (
              <>
                {q.type === 'scale' && (
                  <div className="flex justify-between text-xs text-gray-400 mb-2">
                    <span>{q.scaleLeft}</span>
                    <span>{q.scaleRight}</span>
                  </div>
                )}
                {q.distribution.map(d => (
                  <Bar key={d.option} option={d.option} count={d.count} total={q.answered} />
                ))}
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AdminResults;
